import _ from 'lodash';
import memoizeOne from 'memoize-one';

import Measures from 'shared/measures';
import { activeTalents } from 'shared/character';
import { talentsArmoryToSimc } from 'shared/talents';
import { parseAzeriteEssences, stringifyAzeriteEssences } from 'shared/azeriteEssences';
import { parseSoulbindSimcString } from 'shared/shadowlands';

export const normalizeSlots = gear => {
  const optimizeGearKeyMap = {
    shoulder: 'shoulders',
    wrist: 'wrists',
  };
  return _.mapKeys(
    gear,
    (value, key) => optimizeGearKeyMap[key] || key,
  );
};

export const getTalentInfo = json => {
  const { simbot } = json;
  const character = _.get(simbot, 'meta.rawFormData.character');

  const charTalents = activeTalents(character);

  // use override by default
  let talents = '';
  const talentsOverride = _.get(simbot, 'meta.rawFormData.talents');

  if (talentsOverride && simbot.meta.source === 'armory') {
    talents = talentsOverride;
  } else if (!talents && charTalents && charTalents.calcTalent) {
    talents = talentsArmoryToSimc(charTalents.calcTalent);
  }

  const specName = _.get(charTalents, 'spec.name');

  return {
    talents,
    specName,
    classId: _.get(character, 'class'),
  };
};

export const getEssenceInfo = json => {
  const { simbot } = json;
  const optimizeEssences = _.get(simbot, 'meta.rawFormData.optimize.equippedEssences');
  const characterEssences = _.get(simbot, 'meta.rawFormData.character.azeriteEssences');

  if (optimizeEssences) {
    return {
      raw: optimizeEssences,
      parsed: parseAzeriteEssences(optimizeEssences),
    };
  }

  return {
    raw: stringifyAzeriteEssences(characterEssences),
    parsed: characterEssences,
  };
};

export const getSoulbindInfo = json => {
  const { simbot } = json;

  const characterSoulbinds = _.get(simbot, 'meta.rawFormData.character.soulbinds');

  return _.find(characterSoulbinds, { active: true });
};

// memoizeOne should be sufficient here. Most of the time there will be a few calls back-to-back and then
// the cache can be discarded.
// Lodash memoize is very bad for this as jobserver and flightmaster will retain ALL json they have ever processed
// which is not finite
export const unionActors = memoizeOne((json) => {
  const { sim } = json;
  if (!sim) {
    return [];
  }
  const type = _.get(json, 'simbot.meta.type');
  const slots = [
    'head', 'neck', 'shoulder', 'back', 'chest', 'wrist', 'hands', 'waist',
    'legs', 'feet', 'finger1', 'finger2', 'trinket1', 'trinket2', 'main_hand', 'off_hand',
  ];
  const comboSlotMap = {
    finger1: 'rings',
    finger2: 'rings',
    trinket1: 'trinkets',
    trinket2: 'trinkets',
    main_hand: 'weapons',
    off_hand: 'weapons',
  };
  const armorySlotMap = {
    mainHand: 'main_hand',
    offHand: 'off_hand',
  };
  const stages = _.get(json, 'simbot.numStages', 1);
  const optimizeGear = _.get(json, 'simbot.meta.rawFormData.optimize.gear');
  const baseActor = sim.players[0];
  const baseEssences = getEssenceInfo(json);
  const baseTalents = getTalentInfo(json);
  const baseSoulbind = getSoulbindInfo(json);
  let baseGear = baseActor.gear;
  if (type === 'optimize') {
    const optimizeEquipped = _.get(json, 'simbot.meta.rawFormData.optimize.equippedGear');
    baseGear = {
      head: optimizeEquipped.head[0],
      neck: optimizeEquipped.neck[0],
      shoulder: optimizeEquipped.shoulder[0],
      back: optimizeEquipped.back[0],
      chest: optimizeEquipped.chest[0],
      wrist: optimizeEquipped.wrist[0],
      hands: optimizeEquipped.hands[0],
      waist: optimizeEquipped.waist[0],
      legs: optimizeEquipped.legs[0],
      feet: optimizeEquipped.feet[0],
      finger1: optimizeEquipped.rings[0],
      finger2: optimizeEquipped.rings[1],
      trinket1: optimizeEquipped.trinkets[0],
      trinket2: optimizeEquipped.trinkets[1],
      main_hand: optimizeEquipped.main_hand[0],
      off_hand: optimizeEquipped.off_hand[0],
    };
  } else if (json.simbot?.source === 'armory') {
    baseGear = _.mapKeys(
      json.simbot.meta.rawFormData.character.items,
      (v, k) => armorySlotMap[k] || k,
    );
  } else {
    baseGear = _.get(json, 'simbot.meta.rawFormData.simcItems') || baseGear;
  }
  const actors = _.union(
    _.map(_.get(sim, 'players', []), (actor, actorIdx) => ({
      name: actor.name,
      collected_data: actor.collected_data,
      scale_factors: actor.scale_factors,
      scale_factors_all: actor.scale_factors_all,
      scale_deltas: actor.scale_deltas,
      // 2020-01-15 - Changing this from "gear" to "simcGear" - I think the original intent was to try to have
      // data from Raidbots tools match this format but I've leaned towards using simbot's own metadata for handling
      // gear/talents/essences/etc. So this has mostly been here as an artifact of some early decisions.
      // This is causing weirdness with corruption since SimC's outputs "base corruption" (the sum of corruption
      // without resistance) but I used "corruption" to mean "net corruption"
      // Just going to rename this for now since I don't think any reports on the site use gear from the simc actors
      simcGear: baseGear,
      iterations: _.get(actor, 'collected_data.dps.count', _.get(sim, 'options.iterations')),
      baseActor: actorIdx === 0,
      stats: {
        ..._.get(actor, 'collected_data.buffed_stats.attribute'),
        ..._.get(actor, 'collected_data.buffed_stats.stats'),
      },
      essences: baseEssences.raw,
      talents: baseTalents.talents,
      soulbind: baseSoulbind,
    })),
    _.map(_.get(sim, 'profilesets.results', []), actor => {
      let gear;
      let talents = baseTalents.talents;
      let essences = baseEssences.raw;
      let soulbind;
      if (type === 'optimize') {
        const matches = actor.name.match(/Combo ([0-9]+)/);
        let comboIdx = 0;
        if (matches) {
          comboIdx = parseInt(matches[1], 10) - 1;
        }
        const combo = _.get(json, `simbot.meta.rawFormData.optimize.combinations[${comboIdx}]`);
        if (_.isArray(combo) || combo.gear) {
          const pairs = _.map(
            combo.gear || combo,
            (gearIdx, slotIdx) => {
              const slotName = slots[slotIdx];
              const comboSlotName = comboSlotMap[slotName] || slotName;
              return [
                slotName,
                _.find(optimizeGear[comboSlotName], { origIdx: gearIdx })
                || _.find(optimizeGear[slotName], { origIdx: gearIdx }),
              ];
            },
          );
          gear = _.pickBy(_.fromPairs(pairs));
        } else {
          gear = normalizeSlots(combo);
        }
        talents = combo.talents;
        essences = combo.essences;
        soulbind = combo.soulbind;
      }
      const count = actor.iterations;
      const meanStdDev = actor.stddev / Math.sqrt(count);

      const mainMetric = _.find(Measures, { description: sim.profilesets.metric }) || Measures.dps;
      const collectedData = {
        [mainMetric.field]: {
          count,
          min: actor.min,
          mean: actor.mean,
          max: actor.max,
          std_dev: actor.stddev,
          mean_std_dev: meanStdDev,
        },
      };

      _.each(actor.additional_metrics, data => {
        const measure = _.find(Measures, { description: data.metric });
        if (measure) {
          collectedData[measure.field] = {
            count,
            min: data.min,
            mean: data.mean,
            max: data.max,
            std_dev: data.stddev,
            mean_std_dev: data.stddev / Math.sqrt(data.count),
          };
        }
      });

      return {
        name: actor.name,
        collected_data: collectedData,
        gear,
        iterations: actor.iterations,
        simbot: {
          ...actor.simbot,
          precision: (stages - 1) - _.get(actor, 'simbot.stage', 0),
        },
        stats: _.get(actor, 'overrides.stats'),
        talents,
        essences,
        soulbindRaw: soulbind || undefined,
        soulbind: soulbind ? parseSoulbindSimcString(soulbind) : undefined,
      };
    }),
  );
  return actors;
});

export const getMaxActor = (json, measure) => {
  const actors = unionActors(json);
  const measurePath = `collected_data.${Measures[measure].field}.mean`;
  return _.maxBy(actors, measurePath);
};
export const getMaxMeasure = (json, measure) => {
  const measurePath = `collected_data.${Measures[measure].field}.mean`;
  return _.get(getMaxActor(json, measure), measurePath);
};

export const getBaseActor = (json) => {
  const actors = unionActors(json);
  return actors[0];
};
export const getBaseMeasure = (json, measure) => {
  const measurePath = `collected_data.${Measures[measure].field}.mean`;
  return _.get(getBaseActor(json, measure), measurePath);
};

export const getMaxDps = json => getMaxMeasure(json, 'dps');
export const getBaseDps = json => getBaseMeasure(json, 'dps');

export const getAvgItemLevel = json => {
  let avgItemLevel = _.get(json, 'simbot.meta.rawFormData.character.items.averageItemLevelEquipped');
  if (!avgItemLevel) {
    const baseActorGear = normalizeSlots(_.get(json, 'sim.players[0].gear', {}));
    const maxActorGear = normalizeSlots(_.get(getMaxActor(json, 'dps'), 'gear', {}));
    if (!baseActorGear || !maxActorGear) {
      return null;
    }
    const actorGear = {
      ...baseActorGear,
      ...maxActorGear,
    };

    const itemLevels = _.map(actorGear, item => parseInt((item ? (item.ilevel || item.itemLevel) : 0), 10));

    // bit of a hack - double main hand ilevel if there's no off hand
    // The _real_ solution is to verify the main hand is a two-hander but I don't have access to that data
    // from the report in a bunch of scenarios
    if (actorGear.main_hand && !actorGear.off_hand) {
      const mainHand = actorGear.main_hand;
      itemLevels.push(mainHand.ilevel || mainHand.itemLevel);
    }
    avgItemLevel = _.sum(itemLevels) / itemLevels.length;
  }
  return _.floor(avgItemLevel);
};

export const getEquippedGear = json => {
  let equippedGear;
  const { simbot, sim } = json;
  const basePlayer = _.get(sim, 'players[0]', {});
  const charItems = _.get(simbot, 'meta.rawFormData.character.items');
  const simcItems = _.get(simbot, 'meta.rawFormData.simcItems');
  const avgItemLevel = getAvgItemLevel(json);

  if (!_.isEmpty(simcItems)) {
    equippedGear = simcItems;
  } else if (!_.isEmpty(charItems) && avgItemLevel) {
    equippedGear = _.pickBy(
      charItems,
      _.isObject,
    );
  }
  if (equippedGear && _.get(simbot, 'meta.source') === 'input' && basePlayer.gear) {
    // get correct artifact ilevel from first actor data
    if (equippedGear.main_hand) {
      equippedGear.main_hand.itemLevel = _.get(basePlayer, 'gear.main_hand.ilevel');
    }
    if (equippedGear.off_hand) {
      equippedGear.off_hand.itemLevel = _.get(basePlayer, 'gear.off_hand.ilevel');
    }
  }
  return equippedGear;
};

export const getSimErrorInfo = json => {
  const actor = getMaxActor(json, 'dps');
  const dpsData = _.get(actor, 'collected_data.dps');
  if (!dpsData) {
    return {
      percent: 0,
      error: 0,
    };
  }
  const error = 1.96 * dpsData.mean_std_dev;
  const errorPct = error / dpsData.mean;
  return {
    error,
    percent: errorPct,
  };
};

export const getSimError = json => {
  return getSimErrorInfo(json).percent;
};
